/**
 * iOS-safe body scroll lock for full-screen overlays (mobile menu).
 *
 * `overflow: hidden` on <body> is ignored by iOS Safari — the page behind the
 * overlay keeps scrolling and rubber-banding. Pinning <body> with
 * `position: fixed` is the only reliable way; the current scroll offset is
 * kept in `top` and restored on unlock (instantly, bypassing the landing's
 * `scroll-behavior: smooth`).
 */

type SavedStyles = {
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  overflow: string;
};

let locked = false;
let savedY = 0;
let saved: SavedStyles | null = null;

export function isBodyScrollLocked(): boolean {
  return locked;
}

export function lockBodyScroll(): void {
  if (locked || typeof document === 'undefined') return;
  locked = true;
  const body = document.body;
  savedY = window.scrollY || window.pageYOffset || 0;
  saved = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
  };
  body.style.position = 'fixed';
  body.style.top = `-${savedY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
}

export function unlockBodyScroll(): void {
  if (!locked) return;
  locked = false;
  const body = document.body;
  const s = saved;
  saved = null;
  body.style.position = s?.position ?? '';
  body.style.top = s?.top ?? '';
  body.style.left = s?.left ?? '';
  body.style.right = s?.right ?? '';
  body.style.width = s?.width ?? '';
  body.style.overflow = s?.overflow ?? '';
  window.scrollTo({ top: savedY, left: 0, behavior: 'instant' as ScrollBehavior });
}
